import { ReactNode, useRef, useState } from "react"
import { Link, useForm } from "@inertiajs/react"
import { captureEvent } from "@/lib/posthog"
import Button from "@/components/Button"
import Container from "@/components/Container"
import logoIcon from "@/assets/img/logo-icon.png"
import DefaultLayout from "@/layouts/DefaultLayout"
import ErrorMessage from "@/components/ErrorMessage"
import FideLookupModal from "@/modals/FideLookupModal"
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile"

type Inputs = {
  name: string
  email: string
  fide_id: string
  turnstile_token: string
}

const SignUpPage = () => {
  const { data, setData, post, processing, errors } = useForm<Inputs>({
    name: "",
    email: "",
    fide_id: "",
    turnstile_token: "",
  })

  const turnstileRef = useRef<TurnstileInstance>(null)
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined
  const [showFideLookup, setShowFideLookup] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post("/signup", {
      async: true,
      onSuccess: () => captureEvent("signup_started", { source: "direct" }),
      onError: () => turnstileRef.current?.reset(),
    })
  }

  return (
    <>
    <Container className="flex min-h-[calc(100vh-90px)] flex-1 justify-center pt-12 sm:pt-24">
      <div className="flex w-full max-w-sm flex-col items-center">
        <img
          src={logoIcon}
          alt=""
          width={56}
          height={56}
          className="size-12 rounded-xl object-cover sm:size-14"
        />
        <h1 className="mt-6 text-center text-2xl font-bold text-gray-900 sm:text-3xl">
          Create an account
        </h1>

        <form onSubmit={handleSubmit} className="mt-8 w-full">
          <div className="space-y-5">
            <div>
              <label
                htmlFor="name"
                className="block pb-1.5 text-sm font-medium text-gray-700"
              >
                Name
              </label>
              <input
                type="text"
                id="name"
                autoComplete="name"
                required
                className="focus:ring-chessdrop-600 block w-full rounded-xl border-0 p-3 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
              />
              <ErrorMessage className="mt-1.5" message={errors.name} />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block pb-1.5 text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                type="email"
                id="email"
                autoComplete="email"
                required
                className="focus:ring-chessdrop-600 block w-full rounded-xl border-0 p-3 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                value={data.email}
                onChange={(e) => setData("email", e.target.value)}
              />
              <ErrorMessage className="mt-1.5" message={errors.email} />
            </div>

            <div>
              <div className="flex justify-between">
                <div className="flex flex-1 gap-x-4">
                  <label
                    htmlFor="fide_id"
                    className="block pb-1.5 text-sm font-medium text-gray-700"
                  >
                    FIDE ID
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowFideLookup(true)}
                    className="mb-1.5 text-sm text-gray-600 hover:text-gray-900 hover:underline"
                  >
                    Look up
                  </button>
                </div>
                <span className="text-sm text-gray-600">Optional</span>
              </div>
              <input
                type="number"
                inputMode="numeric"
                id="fide_id"
                className="focus:ring-chessdrop-600 hide-spinners block w-full rounded-xl border-0 p-3 text-gray-900 shadow-xs ring-1 ring-gray-300 ring-inset placeholder:text-gray-400 focus:ring-2 focus:ring-inset"
                value={data.fide_id}
                onChange={(e) => setData("fide_id", e.target.value)}
              />

              <ErrorMessage className="mt-1.5" message={errors.fide_id} />
            </div>
          </div>

          {turnstileSiteKey && (
            <Turnstile
              ref={turnstileRef}
              siteKey={turnstileSiteKey}
              options={{ action: "signup", appearance: "interaction-only" }}
              onSuccess={(token) => setData("turnstile_token", token)}
              className="mt-4"
            />
          )}

          <Button
            loading={processing}
            size="lg"
            type="submit"
            className="mt-6"
            fullWidth
          >
            Create account
          </Button>

          {/* The server may return a `turnstile` error key outside the form fields */}
          <ErrorMessage
            className="mt-3 text-center"
            message={(errors as Record<string, string | undefined>).turnstile}
          />

          <p className="mt-8 text-center text-sm text-gray-600">
            Already have an account?{" "}
            <Link
              href="/login"
              className="text-chessdrop-600 font-medium hover:underline"
            >
              Log in
            </Link>
          </p>
        </form>
      </div>
    </Container>

    <FideLookupModal
      show={showFideLookup}
      onClose={() => setShowFideLookup(false)}
      onSelect={(fideId) => setData("fide_id", fideId)}
      initialQuery={data.name}
    />
    </>
  )
}

export default SignUpPage

SignUpPage.layout = (page: ReactNode) => <DefaultLayout>{page}</DefaultLayout>
